import { useState, useEffect, useRef } from 'react';
import { fetchAnnouncementsPage } from '../utils/api';
import type { Announcement, FilterState, TableSort } from '../types';

export function useTablePage(
  filters: FilterState,
  page: number,
  pageSize: number,
  sort: TableSort,
  findCluster?: number | null,
  filterChangeId?: number,
  clusterIds?: number[] | null,
  enabled = true,
  industriesNarrow?: string[],
) {
  const [rows, setRows] = useState<Announcement[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [resolvedPage, setResolvedPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const abortRef = useRef<AbortController | undefined>(undefined);
  const prevFiltersKey = useRef('');
  const prevFilterChangeId = useRef(filterChangeId ?? 0);

  const clusterIdsKey = clusterIds ? clusterIds.join(',') : '';
  const narrowKey = industriesNarrow ? industriesNarrow.join(',') : '';

  // Determine if this is a filter change (debounce) or page/sort change (immediate)
  const filtersKey = [
    filters.dateFrom,
    filters.dateTo,
    filters.federalStates.join(','),
    filters.investmentTypes.join(','),
    filters.investmentStatuses.join(','),
    filters.industries.join(','),
    filters.companies.join(','),
    filters.search,
    filters.spotlight,
    clusterIdsKey,
    narrowKey,
  ].join('|');

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    const isFilterChange = filtersKey !== prevFiltersKey.current;
    const isFilterChangeById = (filterChangeId ?? 0) !== prevFilterChangeId.current;
    prevFiltersKey.current = filtersKey;
    prevFilterChangeId.current = filterChangeId ?? 0;

    // Skip findCluster on filter changes — it would search for the old cluster in new results
    const effectiveFindCluster = isFilterChangeById ? undefined :
      (findCluster != null ? findCluster : undefined);

    const doFetch = async (signal: AbortSignal) => {
      try {
        setLoading(true);
        const result = await fetchAnnouncementsPage(
          filters, page, pageSize, sort,
          effectiveFindCluster,
          clusterIds ?? undefined,
          signal,
          industriesNarrow,
        );
        setRows(result.rows);
        setTotalCount(result.totalCount);
        setResolvedPage(result.page);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Keep previous data on error
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (isFilterChange) {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => doFetch(controller.signal), 150);
    } else {
      clearTimeout(timerRef.current);
      doFetch(controller.signal);
    }

    return () => {
      clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, [filtersKey, page, pageSize, sort.field, sort.dir, findCluster, filterChangeId, clusterIdsKey, enabled]);

  return { rows, totalCount, page: resolvedPage, loading };
}
